import React, { useState, useRef, useEffect } from "react";
import { Icon } from "@ospweb/sdk-ui";
import { cn } from "utils";
import log from "logbeacon";
import { PortfolioMenuButtonProps } from "./types";
import Translations from "@translate/components/Portfolio/Header/PortfolioMenuButton/PortfolioMenuButton";
import { I18nUtil } from "@utils/i18n";
import { useIsPc } from "@/src/hooks";

const I18n = I18nUtil.createScoped(Translations);

/**
 * PortfolioMenuButton - 投资组合管理菜单按钮组件
 *
 * 提供三点菜单按钮，包含编辑和删除投资组合的功能
 * 支持常态、点击态、禁用态，以及下拉菜单显示
 *
 * @param props - 组件属性
 * @returns JSX元素
 */
export const PortfolioMenuButton: React.FC<PortfolioMenuButtonProps> = ({
  onEdit,
  onDelete,
  disabled = false,
  className,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const popRef = useRef<HTMLDivElement>(null);
  const popTriggerRef = useRef<HTMLButtonElement>(null);
  const [isPc] = useIsPc();
  /**
   * 处理点击外部区域关闭菜单
   */
  useEffect(() => {
    // 确保只在客户端执行
    if (typeof window?.document === "undefined") {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (
        popRef.current &&
        !popRef.current.contains(event.target as Node) &&
        !popTriggerRef?.current?.contains(event.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMenuOpen]);
  /**
   * 处理编辑按钮点击事件
   * 暂时使用console.log输出，后续可替换为实际业务逻辑
   */
  /**
   * 处理编辑按钮点击事件
   */
  const handleEdit = () => {
    log.debug("[文案]Edit portfolio clicked");
    setIsMenuOpen(false); // 关闭下拉菜单
    onEdit?.();
  };

  /**
   * 处理删除按钮点击事件
   */
  const handleDelete = () => {
    log.debug("[文案]Delete portfolio clicked");
    setIsMenuOpen(false); // 关闭下拉菜单
    onDelete?.();
  };

  /**
   * 菜单选项配置
   */
  const menuOptions = [
    {
      id: "edit",
      label: I18n.t("Edit portfolio"),
      icon: "edit-alt",
      onClick: handleEdit,
    },
    {
      id: "delete",
      label: I18n.t("Delete portfolio"),
      icon: "trash",
      onClick: handleDelete,
    },
  ];

  /**
   * 下拉菜单内容
   * 包含编辑和删除投资组合的选项
   */
  const dropdownContent = (
    <div className="bg-background_solid_secondary border border-border_white_transparent10 rounded-2 shadow-lg min-w-[160px] p-2">
      {menuOptions.map((option) => (
        <button
          title={option.label}
          aria-label={option.label}
          data-openreplay-label={`${option.label} Button`}
          id="btn-portfolio-menu-button"
          key={option.id}
          type="button"
          onClick={option.onClick}
          className="w-full flex items-center gap-2 px-2 py-3 text-left rounded-[6px] hover:bg-background_white_overlay5 transition-colors duration-150"
        >
          <Icon
            name={option.icon}
            size={16}
            className="text-content_secondary"
          />
          <span className="body_medium_m text-content_primary">
            {option.label}
          </span>
        </button>
      ))}
    </div>
  );

  return (
    <div className="relative">
      <button
        data-openreplay-label="Action Button"
        id="btn-action-portfoliomenubutton-portfoliomenub-4"
        ref={popTriggerRef}
        type="button"
        disabled={disabled}
        onClick={() => {
          if (!disabled) {
            setIsMenuOpen(!isMenuOpen);
          }
        }}
        className={cn(
          "md:secondary_button tonal_button md:bg-transparent",
          "md:sm_button xs_button",
          "md:size-[36px] size-[28px]",
          "button_m text-content_primary",
          { "!opacity-50": isMenuOpen },
          className,
        )}
        data-testid="portfolio-menu-button"
        aria-label="更多选项"
        aria-expanded={isMenuOpen}
      >
        {/* 三点图标 */}
        <div className="size-[16.667px] flex items-center justify-center">
          <Icon
            name="dots-horizontal-rounded"
            size={isPc ? 20 : 16}
            className="md:text-content_primary text-content_tertiary"
          />
        </div>
      </button>

      {/* 下拉菜单 */}
      {isMenuOpen && (
        <div ref={popRef} className="absolute right-0 top-full mt-2 z-50">
          {dropdownContent}
        </div>
      )}
    </div>
  );
};
